import { clsx } from "clsx";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export function wsUrl() {
  const base = BACKEND_URL.replace(/^http/, "ws");
  return `${base}/api/ws`;
}

let pageUnloading = false;
if (typeof window !== "undefined") {
  const markUnloading = () => { pageUnloading = true; };
  window.addEventListener("pagehide", markUnloading);
  window.addEventListener("beforeunload", markUnloading);
  window.addEventListener("pageshow", (e) => { if (e.persisted) pageUnloading = false; });
}

export function isAbortError(err) {
  return !!err && (err.name === "AbortError" || err.aborted === true);
}

export function createPageAbortController() {
  const controller = new AbortController();
  const abortEarly = () => controller.abort();
  window.addEventListener("pagehide", abortEarly);
  window.addEventListener("beforeunload", abortEarly);
  controller.cleanup = () => {
    window.removeEventListener("pagehide", abortEarly);
    window.removeEventListener("beforeunload", abortEarly);
    controller.abort();
  };
  return controller;
}

function getCsrf() {
  const m = typeof document !== "undefined" && document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/);
  return m ? decodeURIComponent(m[1]) : "";
}

async function request(method, path, body, opts = {}) {
  const fetchOpts = {
    method,
    credentials: "include",
    headers: { "Content-Type": "application/json" },
  };
  if (method !== "GET") fetchOpts.headers["X-CSRF-Token"] = getCsrf();
  if (opts.signal) fetchOpts.signal = opts.signal;
  if (body !== undefined) fetchOpts.body = JSON.stringify(body);
  let res;
  try {
    res = await fetch(`${API}${path}`, fetchOpts);
  } catch (err) {
    // Unload-time teardown rejects with a plain TypeError ("Failed to fetch") instead of an
    // AbortError — map it to an AbortError-like sentinel so callers can silently ignore it.
    if (
      pageUnloading ||
      (opts.signal && opts.signal.aborted) ||
      (typeof document !== "undefined" && document.visibilityState === "hidden")
    ) {
      const abortErr = new Error("The operation was aborted.");
      abortErr.name = "AbortError";
      abortErr.aborted = true;
      throw abortErr;
    }
    throw err;
  }
  const text = await res.text();
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = text;
  }
  if (!res.ok) {
    const detail = data && data.detail !== undefined ? data.detail : res.statusText;
    // Single-active-session: a stale token is kicked. Broadcast so the app can log out + message.
    if (res.status === 401 && detail && typeof detail === "object" && detail.code === "session_conflict" && typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent("proplan:session-kick", { detail }));
    }
    throw { status: res.status, detail };
  }
  return data;
}

export const api = {
  get: (p, opts) => request("GET", p, undefined, opts),
  post: (p, b, opts) => request("POST", p, b, opts),
  put: (p, b, opts) => request("PUT", p, b, opts),
  del: (p, opts) => request("DELETE", p, undefined, opts),
  isAbort: isAbortError,
};

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  if (detail && typeof detail.message === "string") {
    if (Array.isArray(detail.shortages) && detail.shortages.length) {
      const names = detail.shortages.map((s) => `${s.name} (short ${s.missing} ${s.unit || ""})`.trim()).join(", ");
      return `${detail.message}: ${names}`;
    }
    return detail.message;
  }
  return String(detail);
}

export const cx = clsx;
